/**
 * G2eGente - Relatório Comparativo de Perfis
 * Compara o perfil ideal da vaga com os perfis dos candidatos
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout, companyMenuItems } from '../../components/DashboardLayout';
import { jobsAPI, candidatesAPI, evaluationsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  Loader2,
  Users,
  Brain,
  Star,
  Target,
  Heart,
  Lightbulb,
  Crown,
  Award,
  CheckCircle,
  XCircle,
  TrendingUp,
  BarChart3,
  Trophy,
  Medal,
  User,
  FileText,
  Download
} from 'lucide-react';

// DISC colors and info
const DISC_INFO = {
  'D': { 
    name: 'Dominância', 
    color: 'bg-red-500', 
    lightColor: 'bg-red-100', 
    textColor: 'text-red-700',
    description: 'Focado em resultados, direto, competitivo'
  },
  'I': { 
    name: 'Influência', 
    color: 'bg-yellow-500', 
    lightColor: 'bg-yellow-100', 
    textColor: 'text-yellow-700',
    description: 'Sociável, entusiasta, persuasivo'
  },
  'S': { 
    name: 'Estabilidade', 
    color: 'bg-green-500', 
    lightColor: 'bg-green-100', 
    textColor: 'text-green-700',
    description: 'Paciente, confiável, cooperativo'
  },
  'C': { 
    name: 'Conformidade', 
    color: 'bg-blue-500', 
    lightColor: 'bg-blue-100', 
    textColor: 'text-blue-700',
    description: 'Analítico, preciso, sistemático'
  }
};

// Evaluation level icons
const getNivelIcon = (nivel) => {
  const icons = {
    'entrega': Target,
    'comportamento': Heart,
    'cognitivo': Brain,
    'aspiracao': Lightbulb,
    'experiencia': Award,
    'lideranca': Crown
  };
  return icons[nivel] || Target;
};

// DISC Chart Component
const DISCChart = ({ percentages, label, size = 'normal' }) => {
  const sizeClass = size === 'small' ? 'w-24 h-24' : 'w-32 h-32';
  const strokeWidth = size === 'small' ? 6 : 8;
  
  return (
    <div className="flex flex-col items-center">
      <div className={`relative ${sizeClass}`}>
        <svg viewBox="0 0 100 100" className="transform -rotate-90">
          {/* Background circle */}
          <circle
            cx="50" cy="50" r="40"
            fill="none"
            stroke="#e5e7eb"
            strokeWidth={strokeWidth}
          />
          {/* DISC segments */}
          {['D', 'I', 'S', 'C'].map((disc, idx) => {
            const pct = percentages?.[disc] || 0;
            const offset = ['D', 'I', 'S', 'C'].slice(0, idx).reduce((sum, d) => sum + (percentages?.[d] || 0), 0);
            const circumference = 2 * Math.PI * 40;
            const dashLength = (pct / 100) * circumference;
            const dashOffset = circumference - (offset / 100) * circumference;
            
            return (
              <circle
                key={disc}
                cx="50" cy="50" r="40"
                fill="none"
                stroke={DISC_INFO[disc].color.replace('bg-', '')}
                className={DISC_INFO[disc].color}
                strokeWidth={strokeWidth}
                strokeDasharray={`${dashLength} ${circumference}`}
                strokeDashoffset={dashOffset}
                style={{ transition: 'stroke-dasharray 0.5s ease' }}
              />
            );
          })}
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`font-bold ${size === 'small' ? 'text-sm' : 'text-lg'}`}>
            {label || 'DISC'}
          </span>
        </div>
      </div>
      {/* Legend */}
      <div className="flex gap-2 mt-2 flex-wrap justify-center">
        {['D', 'I', 'S', 'C'].map(disc => (
          <div key={disc} className="flex items-center gap-1">
            <div className={`w-2 h-2 rounded-full ${DISC_INFO[disc].color}`} />
            <span className="text-xs">{disc}: {percentages?.[disc] || 0}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Bar comparison component
const ComparisonBar = ({ label, ideal, actual, icon: Icon }) => {
  const diff = actual - ideal;
  const matchPct = Math.max(0, 100 - Math.abs(diff) * 20);
  
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-slate-500" />}
          <span className="text-sm font-medium">{label}</span>
        </div>
        <Badge variant={matchPct >= 80 ? 'default' : matchPct >= 60 ? 'secondary' : 'outline'} 
               className={matchPct >= 80 ? 'bg-green-500' : matchPct >= 60 ? 'bg-amber-500' : ''}>
          {matchPct.toFixed(0)}% match
        </Badge>
      </div>
      <div className="flex gap-2 items-center">
        <div className="flex-1 space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500 w-12">Ideal:</span>
            <Progress value={ideal * 20} className="h-2 flex-1" />
            <span className="text-xs font-medium w-6">{ideal}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500 w-12">Real:</span>
            <Progress value={actual * 20} className="h-2 flex-1" />
            <span className="text-xs font-medium w-6">{actual}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function JobProfileComparison() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [job, setJob] = useState(null);
  const [ranking, setRanking] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [candidateDetail, setCandidateDetail] = useState(null);
  const [candidateEvaluation, setCandidateEvaluation] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    fetchData();
  }, [jobId]);

  const fetchData = async () => {
    try {
      // Fetch job data
      const jobRes = await jobsAPI.getById(jobId);
      setJob(jobRes.data);
      
      // Fetch ranking
      const rankingRes = await jobsAPI.getRanking(jobId);
      setRanking(rankingRes.data.ranking || []);
      
      // Auto-select first candidate if available
      if (rankingRes.data.ranking?.length > 0) {
        const firstCandidate = rankingRes.data.ranking[0];
        setSelectedCandidate(firstCandidate.candidate_id);
        await fetchCandidateDetail(firstCandidate.candidate_id);
      }
    } catch (error) {
      toast.error('Erro ao carregar dados');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCandidateDetail = async (candidateId) => {
    setLoadingDetail(true);
    try {
      // Fetch candidate profile
      const candRes = await candidatesAPI.getById(candidateId);
      setCandidateDetail(candRes.data);
      
      // Fetch candidate evaluation for this job
      const evalRes = await evaluationsAPI.getAll({ candidate_id: candidateId, job_id: jobId });
      if (evalRes.data?.length > 0) {
        setCandidateEvaluation(evalRes.data[0]);
      } else {
        setCandidateEvaluation(null);
      }
    } catch (error) {
      console.error('Error fetching candidate detail:', error);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleSelectCandidate = async (candidateId) => {
    setSelectedCandidate(candidateId);
    await fetchCandidateDetail(candidateId);
  };

  const getPositionBadge = (index) => {
    if (index === 0) return { icon: Trophy, color: 'bg-amber-100 text-amber-700 border-amber-300' };
    if (index === 1) return { icon: Medal, color: 'bg-slate-200 text-slate-700 border-slate-300' };
    if (index === 2) return { icon: Medal, color: 'bg-orange-100 text-orange-700 border-orange-300' };
    return { icon: User, color: 'bg-slate-100 text-slate-600 border-slate-200' };
  };

  const getRankingItem = (candidateId) => {
    return ranking.find(r => r.candidate_id === candidateId);
  };

  const calculateDISCMatch = (idealDisc, candidateDisc) => {
    if (!idealDisc || !candidateDisc) return { match: false, percentage: 0 };
    const ideal = idealDisc.toUpperCase();
    const candidate = candidateDisc.toUpperCase();
    const matchingTraits = ideal.split('').filter(d => candidate.includes(d)).length;
    const percentage = (matchingTraits / ideal.length) * 100;
    return { match: matchingTraits > 0, percentage };
  };

  if (loading) {
    return (
      <DashboardLayout menuItems={companyMenuItems}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
        </div>
      </DashboardLayout>
    );
  }

  if (!job) {
    return (
      <DashboardLayout menuItems={companyMenuItems}>
        <div className="text-center py-12">
          <p className="text-slate-600">Vaga não encontrada</p>
        </div>
      </DashboardLayout>
    );
  }

  const perfilIdeal = job.perfil_ideal;
  const selectedRankingItem = getRankingItem(selectedCandidate);

  return (
    <DashboardLayout menuItems={companyMenuItems}>
      <div className="space-y-6" data-testid="profile-comparison">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(`/company/jobs/${jobId}`)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Relatório Comparativo</h1>
              <p className="text-slate-600">{job.titulo}</p>
            </div>
          </div>
          <Button className="bg-slate-400 text-white cursor-not-allowed" disabled>
            <Download className="w-4 h-4 mr-2" />
            Exportar PDF
          </Button>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-12 gap-6">
          {/* Left Panel - Candidate List */}
          <div className="col-span-3">
            <Card className="sticky top-4">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Candidatos ({ranking.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="max-h-[600px] overflow-y-auto">
                  {ranking.length === 0 ? (
                    <div className="p-4 text-center text-slate-500 text-sm">
                      Nenhum candidato aprovado
                    </div>
                  ) : (
                    ranking.map((item, index) => {
                      const position = getPositionBadge(index);
                      const Icon = position.icon;
                      return (
                        <button
                          key={item.candidate_id}
                          onClick={() => handleSelectCandidate(item.candidate_id)}
                          className={`w-full p-3 text-left border-b last:border-b-0 transition-colors ${
                            selectedCandidate === item.candidate_id 
                              ? 'bg-cyan-50 border-l-4 border-l-cyan-500' 
                              : 'hover:bg-slate-50'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${position.color} border`}>
                              <span className="text-xs font-bold">{index + 1}º</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-slate-900 truncate text-sm">
                                {item.candidate_name}
                              </p>
                              <div className="flex items-center gap-2">
                                <span className={`text-xs font-semibold ${
                                  item.aderencia >= 80 ? 'text-green-600' :
                                  item.aderencia >= 60 ? 'text-amber-600' :
                                  'text-slate-500'
                                }`}>
                                  {item.aderencia}%
                                </span>
                                {item.disc_profile && (
                                  <Badge variant="outline" className="text-xs py-0 px-1">
                                    {item.disc_profile}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            {item.disc_match && (
                              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                            )}
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Panel - Comparison Detail */}
          <div className="col-span-9 space-y-6">
            {/* Warning if no perfil_ideal */}
            {!perfilIdeal?.disc_desejado && !perfilIdeal?.niveis?.length && (
              <Card className="border-amber-200 bg-amber-50">
                <CardContent className="p-6 text-center">
                  <Target className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                  <h3 className="font-semibold text-amber-900">Perfil Ideal não configurado</h3>
                  <p className="text-sm text-amber-700 mt-2">
                    Configure o perfil ideal do candidato para visualizar comparações detalhadas e ranking de aderência.
                  </p>
                  <Button 
                    onClick={() => navigate(`/company/jobs/${jobId}/edit`)}
                    className="mt-4 bg-amber-600 hover:bg-amber-700"
                  >
                    Configurar Perfil Ideal
                  </Button>
                </CardContent>
              </Card>
            )}
            
            {/* Warning if no candidates */}
            {ranking.length === 0 && (perfilIdeal?.disc_desejado || perfilIdeal?.niveis?.length) && (
              <Card className="border-blue-200 bg-blue-50">
                <CardContent className="p-6 text-center">
                  <Users className="w-12 h-12 text-blue-500 mx-auto mb-4" />
                  <h3 className="font-semibold text-blue-900">Nenhum candidato para comparar</h3>
                  <p className="text-sm text-blue-700 mt-2">
                    Convide candidatos para esta vaga na aba "Buscar Talentos" para visualizar o ranking de aderência.
                  </p>
                  <Button 
                    onClick={() => navigate(`/company/jobs/${jobId}/candidates`)}
                    className="mt-4 bg-blue-600 hover:bg-blue-700"
                  >
                    Buscar Candidatos
                  </Button>
                </CardContent>
              </Card>
            )}
            
            {/* Show comparison if we have data */}
            {selectedCandidate && candidateDetail ? (
              <>
                {/* Comparison Header */}
                <Card className="border-slate-200">
                  <CardContent className="p-6">
                    <div className="grid grid-cols-3 gap-8 items-center">
                      {/* Job Profile */}
                      <div className="text-center">
                        <h3 className="font-semibold text-slate-900 mb-4">Perfil Ideal da Vaga</h3>
                        {perfilIdeal?.disc_percentages ? (
                          <DISCChart 
                            percentages={perfilIdeal.disc_percentages} 
                            label={perfilIdeal.disc_desejado || 'DISC'}
                          />
                        ) : (
                          <div className="p-6 bg-slate-50 rounded-lg">
                            <Badge className="text-lg px-4 py-2">
                              {perfilIdeal?.disc_desejado || 'Não definido'}
                            </Badge>
                            {perfilIdeal?.disc_desejado && (
                              <p className="text-xs text-slate-500 mt-2">
                                {DISC_INFO[perfilIdeal.disc_desejado]?.description}
                              </p>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Match Indicator */}
                      <div className="text-center">
                        <div className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center ${
                          selectedRankingItem?.aderencia >= 80 ? 'bg-green-100' :
                          selectedRankingItem?.aderencia >= 60 ? 'bg-amber-100' :
                          'bg-slate-100'
                        }`}>
                          <div className="text-center">
                            <span className={`text-3xl font-bold ${
                              selectedRankingItem?.aderencia >= 80 ? 'text-green-600' :
                              selectedRankingItem?.aderencia >= 60 ? 'text-amber-600' :
                              'text-slate-600'
                            }`}>
                              {selectedRankingItem?.aderencia || 0}%
                            </span>
                            <p className="text-xs text-slate-500">Aderência</p>
                          </div>
                        </div>
                        <div className="mt-4 flex items-center justify-center gap-2">
                          {selectedRankingItem?.disc_match ? (
                            <Badge className="bg-green-500">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              DISC Compatível
                            </Badge>
                          ) : (
                            <Badge variant="outline">
                              <XCircle className="w-3 h-3 mr-1" />
                              DISC Divergente
                            </Badge>
                          )}
                        </div>
                      </div>

                      {/* Candidate Profile */}
                      <div className="text-center">
                        <h3 className="font-semibold text-slate-900 mb-4">
                          {candidateDetail.nome || 'Candidato'}
                        </h3>
                        {candidateDetail.disc_detalhado?.disc_percentages ? (
                          <DISCChart 
                            percentages={candidateDetail.disc_detalhado.disc_percentages}
                            label={candidateDetail.disc_profile || 'DISC'}
                          />
                        ) : (
                          <div className="p-6 bg-slate-50 rounded-lg">
                            <Badge className="text-lg px-4 py-2" variant="outline">
                              {candidateDetail.disc_profile || 'Não avaliado'}
                            </Badge>
                            {candidateDetail.disc_profile && (
                              <p className="text-xs text-slate-500 mt-2">
                                {DISC_INFO[candidateDetail.disc_profile]?.description}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Detailed Comparison Tabs */}
                <Card className="border-slate-200">
                  <Tabs defaultValue="niveis" className="w-full">
                    <CardHeader className="pb-0">
                      <TabsList className="w-full grid grid-cols-3">
                        <TabsTrigger value="niveis">
                          <BarChart3 className="w-4 h-4 mr-2" />
                          Níveis de Avaliação
                        </TabsTrigger>
                        <TabsTrigger value="disc">
                          <Brain className="w-4 h-4 mr-2" />
                          Perfil DISC
                        </TabsTrigger>
                        <TabsTrigger value="resumo">
                          <FileText className="w-4 h-4 mr-2" />
                          Resumo
                        </TabsTrigger>
                      </TabsList>
                    </CardHeader>
                    
                    <CardContent className="pt-6">
                      {/* Níveis de Avaliação Tab */}
                      <TabsContent value="niveis" className="mt-0">
                        {loadingDetail ? (
                          <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-6 h-6 animate-spin" />
                          </div>
                        ) : !candidateEvaluation ? (
                          <div className="text-center py-12 text-slate-500">
                            <Target className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                            <p>Candidato ainda não foi avaliado nesta vaga</p>
                          </div>
                        ) : (
                          <div className="space-y-6">
                            {perfilIdeal?.niveis?.map((idealNivel) => {
                              const evalNivel = candidateEvaluation?.niveis?.find(n => n.nivel === idealNivel.nivel);
                              const Icon = getNivelIcon(idealNivel.nivel);
                              
                              return (
                                <div key={idealNivel.nivel} className="p-4 border rounded-lg">
                                  <div className="flex items-center gap-2 mb-4">
                                    <Icon className="w-5 h-5 text-cyan-600" />
                                    <h4 className="font-semibold text-slate-900">{idealNivel.nome}</h4>
                                    {selectedRankingItem?.nivel_scores?.[idealNivel.nivel] && (
                                      <Badge variant="outline" className="ml-auto">
                                        {selectedRankingItem.nivel_scores[idealNivel.nivel]}% match
                                      </Badge>
                                    )}
                                  </div>
                                  
                                  <div className="grid grid-cols-2 gap-4">
                                    {idealNivel.steps?.map((idealStep) => {
                                      const evalStep = evalNivel?.steps?.find(s => s.nome === idealStep.nome);
                                      return (
                                        <ComparisonBar
                                          key={idealStep.nome}
                                          label={idealStep.nome}
                                          ideal={idealStep.pontuacao_ideal || 0}
                                          actual={evalStep?.pontuacao || 0}
                                        />
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </TabsContent>

                      {/* DISC Tab */}
                      <TabsContent value="disc" className="mt-0">
                        <div className="grid grid-cols-2 gap-8">
                          {/* DISC Breakdown */}
                          <div className="space-y-4">
                            <h4 className="font-semibold text-slate-900">Comparação por Dimensão</h4>
                            {['D', 'I', 'S', 'C'].map(disc => {
                              const idealPct = perfilIdeal?.disc_percentages?.[disc] || 
                                              (perfilIdeal?.disc_desejado?.includes(disc) ? 50 : 25);
                              const candPct = candidateDetail?.disc_detalhado?.disc_percentages?.[disc] || 
                                             (candidateDetail?.disc_profile?.includes(disc) ? 50 : 25);
                              const info = DISC_INFO[disc];
                              
                              return (
                                <div key={disc} className={`p-4 rounded-lg ${info.lightColor}`}>
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                      <div className={`w-8 h-8 rounded-full ${info.color} flex items-center justify-center text-white font-bold`}>
                                        {disc}
                                      </div>
                                      <div>
                                        <span className="font-medium">{info.name}</span>
                                        <p className="text-xs text-slate-500">{info.description}</p>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs w-12 text-slate-500">Ideal:</span>
                                      <Progress value={idealPct} className="h-2 flex-1" />
                                      <span className="text-xs font-medium w-10">{idealPct}%</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs w-12 text-slate-500">Real:</span>
                                      <Progress value={candPct} className="h-2 flex-1" />
                                      <span className="text-xs font-medium w-10">{candPct}%</span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {/* DISC Insights */}
                          <div className="space-y-4">
                            <h4 className="font-semibold text-slate-900">Insights</h4>
                            
                            <Card className="border-slate-200">
                              <CardContent className="p-4">
                                <h5 className="font-medium text-slate-900 mb-2">Perfil Buscado</h5>
                                <Badge className="text-lg px-3 py-1">
                                  {perfilIdeal?.disc_desejado || 'Não definido'}
                                </Badge>
                                {perfilIdeal?.disc_desejado && (
                                  <p className="text-sm text-slate-600 mt-2">
                                    {DISC_INFO[perfilIdeal.disc_desejado[0]]?.description}
                                  </p>
                                )}
                              </CardContent>
                            </Card>

                            <Card className="border-slate-200">
                              <CardContent className="p-4">
                                <h5 className="font-medium text-slate-900 mb-2">Perfil do Candidato</h5>
                                <Badge variant="outline" className="text-lg px-3 py-1">
                                  {candidateDetail?.disc_profile || 'Não avaliado'}
                                </Badge>
                                {candidateDetail?.disc_profile && (
                                  <p className="text-sm text-slate-600 mt-2">
                                    {DISC_INFO[candidateDetail.disc_profile[0]]?.description}
                                  </p>
                                )}
                              </CardContent>
                            </Card>

                            {selectedRankingItem?.disc_match ? (
                              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                                <div className="flex items-center gap-2 text-green-700">
                                  <CheckCircle className="w-5 h-5" />
                                  <span className="font-medium">Perfil DISC Compatível</span>
                                </div>
                                <p className="text-sm text-green-600 mt-2">
                                  O candidato possui características comportamentais alinhadas com o perfil desejado para a vaga.
                                </p>
                              </div>
                            ) : (
                              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                                <div className="flex items-center gap-2 text-amber-700">
                                  <TrendingUp className="w-5 h-5" />
                                  <span className="font-medium">Perfil DISC Diferente</span>
                                </div>
                                <p className="text-sm text-amber-600 mt-2">
                                  O candidato possui um perfil comportamental diferente do ideal, mas pode complementar a equipe.
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </TabsContent>

                      {/* Resumo Tab */}
                      <TabsContent value="resumo" className="mt-0">
                        <div className="grid grid-cols-2 gap-6">
                          {/* Left - Candidate Summary */}
                          <div className="space-y-4">
                            <h4 className="font-semibold text-slate-900">Dados do Candidato</h4>
                            
                            <div className="space-y-3">
                              <div className="p-3 bg-slate-50 rounded-lg">
                                <span className="text-xs text-slate-500">Nome</span>
                                <p className="font-medium">{candidateDetail?.nome || 'N/A'}</p>
                              </div>
                              <div className="p-3 bg-slate-50 rounded-lg">
                                <span className="text-xs text-slate-500">Cargo Pretendido</span>
                                <p className="font-medium">{candidateDetail?.cargo || 'N/A'}</p>
                              </div>
                              <div className="p-3 bg-slate-50 rounded-lg">
                                <span className="text-xs text-slate-500">Localização</span>
                                <p className="font-medium">{candidateDetail?.localizacao || 'N/A'}</p>
                              </div>
                              {candidateDetail?.competencias?.length > 0 && (
                                <div className="p-3 bg-slate-50 rounded-lg">
                                  <span className="text-xs text-slate-500">Competências</span>
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {candidateDetail.competencias.slice(0, 5).map((comp, idx) => (
                                      <Badge key={idx} variant="outline" className="text-xs">
                                        {comp}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Right - Match Summary */}
                          <div className="space-y-4">
                            <h4 className="font-semibold text-slate-900">Resumo da Análise</h4>
                            
                            <div className={`p-6 rounded-lg text-center ${
                              selectedRankingItem?.aderencia >= 80 ? 'bg-green-50 border border-green-200' :
                              selectedRankingItem?.aderencia >= 60 ? 'bg-amber-50 border border-amber-200' :
                              'bg-slate-50 border border-slate-200'
                            }`}>
                              <span className={`text-5xl font-bold ${
                                selectedRankingItem?.aderencia >= 80 ? 'text-green-600' :
                                selectedRankingItem?.aderencia >= 60 ? 'text-amber-600' :
                                'text-slate-600'
                              }`}>
                                {selectedRankingItem?.aderencia || 0}%
                              </span>
                              <p className="text-sm text-slate-600 mt-2">Aderência ao Perfil Ideal</p>
                            </div>

                            {candidateEvaluation && (
                              <div className="space-y-2">
                                <div className="flex justify-between text-sm">
                                  <span className="text-slate-600">Recomendação:</span>
                                  <Badge className={
                                    candidateEvaluation.recomendacao === 'contratar' ? 'bg-green-500' :
                                    candidateEvaluation.recomendacao === 'revisar' ? 'bg-amber-500' :
                                    'bg-red-500'
                                  }>
                                    {candidateEvaluation.recomendacao === 'contratar' ? 'Contratar' :
                                     candidateEvaluation.recomendacao === 'revisar' ? 'Revisar' :
                                     'Não Contratar'}
                                  </Badge>
                                </div>
                                <div className="flex justify-between text-sm">
                                  <span className="text-slate-600">Média Geral:</span>
                                  <span className="font-medium">{candidateEvaluation.media_geral?.toFixed(1) || 'N/A'}/5</span>
                                </div>
                              </div>
                            )}

                            {candidateEvaluation?.pontos_fortes?.length > 0 && (
                              <div className="p-3 bg-green-50 rounded-lg">
                                <span className="text-xs text-green-700 font-medium">Pontos Fortes</span>
                                <ul className="text-sm text-green-600 mt-1 list-disc list-inside">
                                  {candidateEvaluation.pontos_fortes.slice(0, 3).map((pf, idx) => (
                                    <li key={idx}>{pf}</li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {candidateEvaluation?.pontos_melhoria?.length > 0 && (
                              <div className="p-3 bg-amber-50 rounded-lg">
                                <span className="text-xs text-amber-700 font-medium">Pontos de Melhoria</span>
                                <ul className="text-sm text-amber-600 mt-1 list-disc list-inside">
                                  {candidateEvaluation.pontos_melhoria.slice(0, 3).map((pm, idx) => (
                                    <li key={idx}>{pm}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      </TabsContent>
                    </CardContent>
                  </Tabs>
                </Card>
              </>
            ) : ranking.length > 0 && !selectedCandidate ? (
              <Card className="border-slate-200">
                <CardContent className="p-12 text-center">
                  <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-semibold text-slate-900">Selecione um candidato</h3>
                  <p className="text-sm text-slate-600 mt-2">
                    Escolha um candidato na lista à esquerda para ver a comparação detalhada
                  </p>
                </CardContent>
              </Card>
            ) : null}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
